import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { login, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const Login = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login({ email, password });
      setToken(data.access_token);
      setUser(data.user);
      navigate("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6 grain" data-testid="login-page">
      <div className="relative z-10 w-full max-w-md border border-white/10 bg-surface p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <img src="/round-logo.png" alt="NORTHEASTGO" className="h-10 w-10 rounded-full" />
          <div className="flex items-center gap-2 text-gold">
            <Lock className="h-4 w-4" />
            <p className="eyebrow">Sales Console</p>
          </div>
        </div>
        <img src="/master-logo.png" alt="NORTHEASTGO" className="mt-5 h-12 w-auto" />
        <p className="mt-3 text-sm text-white/50">Sign in to manage leads &amp; consultations.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/70">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email-input" className="rounded-none border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/70">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" className="rounded-none border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="h-12 w-full rounded-none bg-gold text-sm font-bold uppercase tracking-[0.1em] text-ink hover:bg-gold-hover">
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
